import { FC, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Typography,
} from '@mui/material';
import { api } from 'services/api';
import { getPgErrorCode, getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { usePgCall } from '../common/usePgCall';
import { RunSummary } from './RunDetailsDialog';
import { useRecurringRun } from './useRecurringRun';

type TRunNowDialogProps = {
	open: boolean;
	onClose: () => void;
};

/**
 * "Run now" (03 §4.6): runs the logic of the current day by hand. A dry run comes first and charges
 * nothing; only after it finished may the admin confirm the real run. The gateway refuses a real run
 * while another one of today is still running (409 `run_in_progress`).
 */
export const RunNowDialog: FC<TRunNowDialogProps> = ({ open, onClose }) => {
	const [dryRunId, setDryRunId] = useState<string | null>(null);
	const [realRunId, setRealRunId] = useState<string | null>(null);
	const [confirmed, setConfirmed] = useState(false);
	const [startError, setStartError] = useState<string | null>(null);

	const startCall = usePgCall(api.paymentGateway.startRecurringRun, {
		fallbackError: 'Failed to start the run',
		silent: true,
	});

	const dryRun = useRecurringRun(dryRunId);
	const realRun = useRecurringRun(realRunId);

	// Every opening starts from the dry run again.
	const [wasOpen, setWasOpen] = useState(false);
	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) {
			setDryRunId(null);
			setRealRunId(null);
			setConfirmed(false);
			setStartError(null);
		}
	}

	const start = async (isDryRun: boolean) => {
		setStartError(null);
		const result = await startCall.run({ dryRun: isDryRun });
		if (!result.ok) {
			const code = getPgErrorCode(result.error);
			setStartError(
				code === 'run_in_progress'
					? getPgErrorMessage(result.error, 'A run of today is still in progress. Try again later.')
					: getPgErrorMessage(result.error, 'Failed to start the run'),
			);
			return;
		}
		if (isDryRun) setDryRunId(result.data.runId);
		else setRealRunId(result.data.runId);
	};

	const dryRunFinished = !!dryRun.run && dryRun.run.status !== 'running';
	const dryRunSucceeded = dryRun.run?.status === 'completed';
	const canStartReal = dryRunFinished && dryRunSucceeded && confirmed && !realRunId;

	return (
		<Dialog open={open} onClose={startCall.loading ? undefined : onClose} maxWidth="md" fullWidth>
			<DialogTitle color="primary">Run the monthly cycle now</DialogTitle>
			<DialogContent>
				<Typography variant="body2" sx={{ mb: 2 }}>
					Runs today&apos;s logic of the schedule (blackout days, Direct Debit day, card attempt
					days and the debt transfer rules apply as usual). Start with a dry run: it charges nothing
					and shows what would happen.
				</Typography>

				<Typography variant="subtitle2" color="primary">
					1. Dry run
				</Typography>
				{!dryRunId && (
					<Button
						variant="contained"
						sx={{ my: 1 }}
						onClick={() => void start(true)}
						disabled={startCall.loading}
						startIcon={startCall.loading ? <CircularProgress size={16} /> : undefined}
					>
						Start dry run
					</Button>
				)}
				{dryRunId && (
					<Box sx={{ my: 1 }}>
						{dryRun.error && <Alert severity="error">{dryRun.error}</Alert>}
						{dryRun.run ? <RunSummary run={dryRun.run} /> : <CircularProgress size={20} />}
					</Box>
				)}

				<Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>
					2. Real run
				</Typography>
				{!realRunId ? (
					<Box sx={{ my: 1 }}>
						{dryRunFinished && !dryRunSucceeded && (
							<Alert severity="error" sx={{ mb: 1 }}>
								The dry run failed: fix the cause before charging customers.
							</Alert>
						)}
						<FormControlLabel
							control={
								<Checkbox
									checked={confirmed}
									disabled={!dryRunSucceeded}
									onChange={(e) => setConfirmed(e.target.checked)}
								/>
							}
							label="I checked the dry run and want to charge customers now"
						/>
						<Box>
							<Button
								variant="contained"
								color="error"
								onClick={() => void start(false)}
								disabled={!canStartReal || startCall.loading}
								startIcon={startCall.loading ? <CircularProgress size={16} /> : undefined}
							>
								Run for real
							</Button>
						</Box>
					</Box>
				) : (
					<Box sx={{ my: 1 }}>
						{realRun.error && <Alert severity="error">{realRun.error}</Alert>}
						{realRun.run ? <RunSummary run={realRun.run} /> : <CircularProgress size={20} />}
						<Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
							You may close this window: the run continues on the gateway.
						</Typography>
					</Box>
				)}

				{startError && (
					<Alert severity="error" sx={{ mt: 1 }}>
						{startError}
					</Alert>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={startCall.loading}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};
